// The signed-in layout: loads GET /me, resolves the org/store from the route (a slug outside the
// user's memberships renders "not found", mirroring the API's 404 for foreign tenants), and renders
// sidebar/rail, top bar, bottom bar and the command palette around the page.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Outlet, useParams } from "@tanstack/react-router";
import { t } from "@ady/i18n";
import {
  BusyRegion,
  CommandPalette,
  ErrorState,
  Logo,
  SkeletonText,
  StatePanel,
  usePaletteShortcut,
  type PaletteCommand,
} from "@ady/ui";
import { LegalGate } from "../auth/LegalGate";
import { ApiError } from "../api/client";
import { errorMessage, isUnauthenticated } from "../api/errors";
import { useMe } from "../api/queries";
import type { MeResponse } from "../api/types";
import { applyTheme, readTheme } from "../theme";
import { TID } from "../testids";
import { AppLink, useGo } from "./AppLink";
import { BottomBar } from "./BottomBar";
import { GroupBar } from "./GroupBar";
import { TopRow } from "./TopRow";
import {
  defaultStore,
  isMocked,
  rememberStore,
  ShellContext,
  type ShellContextValue,
} from "./context";
import { can, findItem, hrefFor, visibleNav } from "./nav";
import { useIdeWindow } from "./ideWindow";
import { Sidebar } from "./Sidebar";
import { AccountMenu, StoreSwitcherDialog, TopBar } from "./TopBar";
import { WalletTopupProvider } from "../billing/WalletTopup";
import { PlanGate, ShellBanners } from "./ControlCentre";
import { LiveCelebration } from "./LiveCelebration";

/** A mock test switch that is on (?mockCatalog=…), set by mocks/modules/catalog.ts. */
function useMockSwitch(): string | null {
  const read = () => (window as { __adyMockCatalog?: string }).__adyMockCatalog ?? "normal";
  const [mode, setMode] = useState(read);
  useEffect(() => {
    const on = () => setMode(read());
    window.addEventListener("ady:mock-switch", on);
    return () => window.removeEventListener("ady:mock-switch", on);
  }, []);
  return mode === "normal" ? null : mode;
}

export function MockBanner() {
  const testSwitch = useMockSwitch();
  if (!isMocked()) return null;
  return (
    <p
      data-testid={TID.mockBanner}
      role="note"
      className="bg-warning-bg px-4 py-1.5 text-center text-xs font-medium text-warning-fg"
    >
      {testSwitch ? (
        <>
          <strong>{t("shell.mockSwitch", { mode: testSwitch })}</strong>{" "}
          <button
            type="button"
            className="underline underline-offset-2"
            onClick={() => {
              // Drop the switch from the address and reload: switches live in memory only.
              const u = new URL(location.href);
              u.searchParams.delete("mockCatalog");
              location.replace(u.toString());
            }}
          >
            {t("shell.mockSwitchOff")}
          </button>{" "}
          ·{" "}
        </>
      ) : null}
      {t("shell.mockBanner")}{" "}
      <button
        type="button"
        className="underline underline-offset-2"
        onClick={() => {
          // `import.meta.env.DEV` is false in production builds, so this import is removed there.
          if (import.meta.env.DEV && window.confirm(t("shell.mockResetConfirm")))
            void import("../mocks/persist").then((m) => m.resetMockData());
        }}
      >
        {t("shell.mockReset")}
      </button>
    </p>
  );
}

function BootScreen({ children }: { children?: ReactNode }) {
  return (
    <div
      className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-canvas p-6"
      aria-busy={children ? undefined : true}
    >
      <Logo
        variant="mark"
        height={40}
        className="animate-pulse motion-reduce:animate-none"
        decorative={Boolean(children)}
      />
      {children ?? <span className="ady-sr-only">{t("shell.loadingApp")}</span>}
    </div>
  );
}

function usePaletteCommands(ctx: Omit<ShellContextValue, "openPalette">): PaletteCommand[] {
  const go = useGo();
  const { membership, orgSlug, storeSlug } = ctx;
  return useMemo(() => {
    const gotos: PaletteCommand[] = visibleNav(membership).flatMap((g) =>
      g.items.map((item) => ({
        id: `go-${item.id}`,
        section: "goTo" as const,
        label: t(item.label),
        keywords: t(g.label),
        icon: g.icon,
        run: () => go(hrefFor(item, orgSlug, storeSlug)),
      })),
    );
    const needs = (perm: string) => t("palette.needsPermission", { permission: perm });
    const pos = findItem("pos");
    const actions: PaletteCommand[] = [
      {
        id: "switch-store",
        section: "actions",
        label: t("palette.actions.switchStore"),
        icon: "store",
        run: ctx.openStoreSwitcher,
      },
      {
        id: "open-pos",
        section: "actions",
        label: t("palette.actions.openPos"),
        icon: "sell",
        unavailableReason: can(membership, ["pos.sell"]) ? undefined : needs("pos.sell"),
        run: () => pos && go(hrefFor(pos, orgSlug, storeSlug)),
      },
      {
        id: "create-order",
        section: "actions",
        label: t("palette.actions.createOrder"),
        icon: "plus",
        unavailableReason: can(membership, ["orders.create"])
          ? t("palette.later")
          : needs("orders.create"),
        run: () => undefined,
      },
      {
        id: "add-product",
        section: "actions",
        label: t("palette.actions.addProduct"),
        icon: "plus",
        unavailableReason: can(membership, ["products.write"])
          ? t("palette.later")
          : needs("products.write"),
        run: () => undefined,
      },
      {
        id: "toggle-theme",
        section: "actions",
        label: t("palette.actions.toggleTheme"),
        icon: "sun",
        run: () => applyTheme(readTheme() === "dark" ? "light" : "dark"),
      },
      {
        id: "security",
        section: "goTo",
        label: t("shell.securityLink"),
        icon: "shield",
        run: () => go("/account/security"),
      },
    ];
    return [...actions, ...gotos];
  }, [membership, orgSlug, storeSlug, go, ctx.openStoreSwitcher]);
}

function Shell({ value }: { value: Omit<ShellContextValue, "openPalette" | "openStoreSwitcher"> }) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const openStoreSwitcher = useCallback(() => setSwitcherOpen(true), []);
  usePaletteShortcut(openPalette);
  useEffect(() => rememberStore(value.orgSlug, value.storeSlug), [value.orgSlug, value.storeSlug]);
  const ctx = { ...value, openPalette, openStoreSwitcher };
  const commands = usePaletteCommands(ctx);
  const ide = useIdeWindow();
  if (ide)
    return (
      <ShellContext.Provider value={ctx}>
        <WalletTopupProvider>
          <main id="main" className="h-dvh bg-canvas" data-testid={TID.shell}>
            <PlanGate>
              <Outlet />
            </PlanGate>
          </main>
        </WalletTopupProvider>
      </ShellContext.Provider>
    );
  return (
    <ShellContext.Provider value={ctx}>
      <WalletTopupProvider>
        <div className="min-h-dvh bg-canvas" data-testid={TID.shell}>
          <a href="#main" className="ady-skip-link" data-testid={TID.skipLink}>
            {t("common.skipToContent")}
          </a>
          <MockBanner />
          <div className="flex">
            <Sidebar />
            <div className="flex min-w-0 flex-1 flex-col">
              <TopBar />
              <main
                id="main"
                tabIndex={-1}
                className="mx-auto w-full max-w-(--container-content) flex-1 px-4 pt-6 pb-[calc(var(--ady-bottom-bar-h)+32px)] outline-none md:px-6 md:pb-12 lg:px-8"
              >
                {/* D-065: support access, suspension and pinned messages; locked features. */}
                <ShellBanners />
                <TopRow>
                  <GroupBar />
                </TopRow>
                <PlanGate>
                  <Outlet />
                </PlanGate>
              </main>
            </div>
          </div>
          <BottomBar />
          <LiveCelebration />
          <CommandPalette
            open={paletteOpen}
            onOpenChange={setPaletteOpen}
            commands={commands}
            recordsNote={t("palette.recordsPending")}
          />
          <StoreSwitcherDialog open={switcherOpen} onClose={() => setSwitcherOpen(false)} />
        </div>
      </WalletTopupProvider>
    </ShellContext.Provider>
  );
}

export function AppLayout() {
  const me = useMe();
  const params = useParams({ strict: false }) as { org?: string; store?: string };
  const go = useGo();
  const unauth = me.isError && isUnauthenticated(me.error);
  useEffect(() => {
    if (unauth) go(`/sign-in?next=${encodeURIComponent(location.pathname)}`);
  }, [unauth, go]);

  if (me.isPending || unauth) {
    return <BootScreen />;
  }
  if (me.isError) {
    return (
      <BootScreen>
        <ErrorState
          size="card"
          body={errorMessage(me.error)}
          requestId={me.error instanceof ApiError ? me.error.requestId : undefined}
          onRetry={() => void me.refetch()}
        />
      </BootScreen>
    );
  }
  const data = me.data;
  const fallback = defaultStore(data);
  const orgSlug = params.org ?? fallback?.org;
  const membership = data.memberships.find((m) => m.org.slug === orgSlug);
  const storeSlug =
    params.store && membership?.stores.some((s) => s.slug === params.store)
      ? params.store
      : membership?.stores[0]?.slug;
  const store = membership?.stores.find((s) => s.slug === storeSlug);
  if ((!membership || !store) && !params.org && location.pathname.startsWith("/account/")) {
    // Account pages (security, sessions) work before any business exists (AUTH-001).
    return (
      <LegalGate>
        <AccountLayout me={data} />
      </LegalGate>
    );
  }
  if (!membership || !store || !orgSlug || !storeSlug) {
    return (
      <BootScreen>
        {fallback ? (
          <StatePanel
            kind="error"
            size="card"
            title={t("errors.notFound")}
            action={{
              label: t("common.continue"),
              onClick: () => go(`/${fallback.org}/${fallback.store}/today`),
            }}
          />
        ) : (
          // Nothing to open yet: no business, or a business without a store (IA §6.1).
          <StatePanel
            kind="not-set-up"
            size="card"
            title={t("auth.needsOrgTitle")}
            body={t("auth.needsOrgBody")}
            action={{ label: t("onboarding.title"), onClick: () => go("/onboarding") }}
          />
        )}
      </BootScreen>
    );
  }
  return (
    <LegalGate>
      <Shell value={{ me: data, membership, store, orgSlug, storeSlug, mocked: isMocked() }} />
    </LegalGate>
  );
}

/** A slim layout for account pages when the user has no store to open yet. */
function AccountLayout({ me }: { me: MeResponse }) {
  return (
    <div className="min-h-dvh bg-canvas" data-testid={TID.accountLayout}>
      <a href="#main" className="ady-skip-link" data-testid={TID.skipLink}>
        {t("common.skipToContent")}
      </a>
      <MockBanner />
      <header className="sticky top-0 z-(--ady-z-sticky) flex h-16 items-center gap-3 border-b border-subtle bg-surface px-4">
        {/* Show/hide on wrappers: the logo's own display rule beats `hidden` on the SVG. */}
        <span className="inline-flex sm:hidden">
          <Logo variant="mark" height={28} />
        </span>
        <span className="hidden sm:inline-flex">
          <Logo variant="lockup" height={26} />
        </span>
        <div className="ms-auto flex min-w-0 items-center gap-2">
          <AppLink
            href="/onboarding"
            className="flex min-h-11 min-w-0 items-center rounded-sm px-2 text-sm font-semibold text-link hover:bg-hover"
          >
            {/* Truncates at 360 px instead of overlapping the account menu. */}
            <span className="truncate">{t("auth.needsOrgTitle")}</span>
          </AppLink>
          <div className="shrink-0">
            <AccountMenu me={me} />
          </div>
        </div>
      </header>
      <main
        id="main"
        tabIndex={-1}
        className="mx-auto w-full max-w-(--container-content) px-4 pt-6 pb-12 outline-none md:px-6 lg:px-8"
      >
        <Outlet />
      </main>
    </div>
  );
}

/** Shared busy skeleton for pages. */
export function PageSkeleton() {
  return (
    <BusyRegion busy skeleton={<SkeletonText lines={6} />}>
      {null}
    </BusyRegion>
  );
}
